import { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useTheme } from "../hooks/useTheme";
import { setPreviewMode } from "./PreviewBanner";
import styles from "./BottomNav.module.css";

const IconHome = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
);
const IconCheck = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
);
const IconFamily = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
);
const IconStore = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
);
const IconEye = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
);
const IconSignOut = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
);
const IconMore = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>
);

const IconGift = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v10H4V12"/><rect x="2" y="7" width="20" height="5"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
);
const IconBank = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="10" width="18" height="11"/><path d="M3 10l9-7 9 7"/><line x1="12" y1="10" x2="12" y2="21"/><line x1="7" y1="14" x2="7" y2="21"/><line x1="17" y1="14" x2="17" y2="21"/></svg>
);
const IconValue = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
);

export default function BottomNav({ pendingCount = 0 }: { pendingCount?: number }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { clearAuth } = useAuthStore();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  function handleSignOut() {
    clearAuth();
    navigate("/");
  }

  // Close More sheet when clicking outside
  useEffect(() => {
    if (!moreOpen) return;
    function handleClick(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [moreOpen]);

  return (
    <>
      {/* More sheet overlay backdrop */}
      {moreOpen && (
        <div className={styles.backdrop} onClick={() => setMoreOpen(false)} />
      )}

      {/* More sheet panel */}
      {moreOpen && (
        <div className={styles.moreSheet} ref={moreRef}>
          <div className={styles.moreSheetHandle} />
          <button
            className={styles.moreItem}
            onClick={() => { setMoreOpen(false); navigate("/parent/gift-points"); }}
          >
            <IconGift />
            <span>Gift {theme.pointNamePlural}</span>
          </button>
          <button
            className={styles.moreItem}
            onClick={() => { setMoreOpen(false); navigate("/parent/points-bank"); }}
          >
            <IconBank />
            <span>{theme.pointBankTitle}</span>
          </button>
          <button
            className={styles.moreItem}
            onClick={() => { setMoreOpen(false); navigate("/parent/points-value"); }}
          >
            <IconValue />
            <span>{theme.pointNamePlural} Value</span>
          </button>
          <div className={styles.moreDivider} />
          <button
            className={styles.moreItem}
            onClick={() => { setMoreOpen(false); setPreviewMode(); navigate("/kid"); }}
          >
            <IconEye />
            <span>Kid View</span>
          </button>
          <div className={styles.moreDivider} />
          <button
            className={`${styles.moreItem} ${styles.moreItemDanger}`}
            onClick={() => { setMoreOpen(false); handleSignOut(); }}
          >
            <IconSignOut />
            <span>Sign Out</span>
          </button>
        </div>
      )}

      <nav className={styles.bar}>
        <NavLink
          to="/parent"
          end
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ""}`}
        >
          <IconHome />
          <span>Home</span>
        </NavLink>

        <NavLink
          to="/parent/challenges"
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ""}`}
        >
          <div style={{ position: "relative", display: "inline-flex" }}>
            <IconCheck />
            {pendingCount > 0 && (
              <span className={styles.badge}>{pendingCount > 9 ? "9+" : pendingCount}</span>
            )}
          </div>
          <span>{theme.challengeNamePlural}</span>
        </NavLink>

        <NavLink
          to="/parent/family-hub"
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ""}`}
        >
          <IconFamily />
          <span>Family</span>
        </NavLink>

        <NavLink
          to="/parent/store"
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ""}`}
        >
          <IconStore />
          <span>Store</span>
        </NavLink>

        <button
          className={`${styles.tab} ${moreOpen ? styles.moreActive : ""}`}
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
          aria-label="More options"
        >
          <IconMore />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}
